import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Chart } from 'klinecharts';
import type { Timeframe, WatchlistEntry, CrosshairData, IndicatorTemplate, ScanRuleConfig, RSLineConfig, CorporateEvent, PriceAdjustmentType, CupHandleSettings, WilliamOneilSellSettings } from '@/types';
import type { SellEvent } from '@/indicators/williamOneilSell';
import { isUserSubPaneIndicator } from '@/components/Chart/indicatorDefs';
import { isMobileViewport } from '@/hooks/useIsMobile';

// Kept in sync with DEFAULT_CUP_HANDLE_SETTINGS in indicators/cupHandle.ts.
// Duplicated (rather than imported) to avoid a circular import — cupHandle.ts
// itself imports useChartStore to read these settings inside calc().
/** Bounds (px) for dragging a sub-pane's height: the pane's floor and the price pane's floor. */
export const SUBPANE_MIN_HEIGHT = 40;
export const CANDLE_MIN_HEIGHT = 120;

const DEFAULT_CUP_HANDLE_SETTINGS: CupHandleSettings = {
  pivotBars: 9,
  rimWindowBars: 25,
  priorLegBars: 60,
  minLegUpPct: 0.20,
  minLengthBars: 30,
  maxLengthBars: 325,
  maxDepthPct: 0.5,
  handleTriggerPct: 0.08,
  maxHandleDepthPct: 0.5,
  minHandleBars: 5,
  maxHandleBars: 65,
  minConfidenceToShow: 0,
};

// Kept in sync with DEFAULT_WILLIAM_ONEIL_SELL_SETTINGS in
// indicators/williamOneilSell.ts — same circular-import reason as above.
const DEFAULT_WILLIAM_ONEIL_SELL_SETTINGS: WilliamOneilSellSettings = {
  ma50Break:        { enabled: true, maPeriod: 50, volMultiplier: 1.4, avgVolPeriod: 50 },
  ma200Break:       { enabled: true, maPeriod: 200 },
  climaxTop:        { enabled: true, lookbackBars: 252, minAdvancePct: 0.25, volMultiplier: 1.5 },
  exhaustionGap:    { enabled: true, minGapPct: 0.02, minAdvancePct: 0.25, advanceLookbackBars: 60, volMultiplier: 1.5 },
  distributionDays: { enabled: true, windowBars: 25, minDownPct: 0.002, volMultiplier: 1.0, thresholdCount: 5 },
  pivotStop:        { enabled: true, stopPct: 0.075, pivotLookbackBars: 130, pivotBars: 9 },
};

// Debounce timers for settings → /api/user/settings sync, kept at module
// scope since store actions aren't React components (no useRef available).
let cupHandleSyncTimer: ReturnType<typeof setTimeout> | null = null;
let williamOneilSellSyncTimer: ReturnType<typeof setTimeout> | null = null;

interface ChartState {
  // Active symbol being charted
  symbol: string;
  setSymbol: (symbol: string) => void;

  // Active timeframe
  timeframe: Timeframe;
  setTimeframe: (tf: Timeframe) => void;

  // UI theme
  theme: 'dark' | 'light';
  toggleTheme: () => void;

  // Watchlist (persisted)
  watchlist: WatchlistEntry[];
  addToWatchlist: (entry: WatchlistEntry) => void;
  removeFromWatchlist: (symbol: string) => void;
  setWatchlist: (entries: WatchlistEntry[]) => void;

  // Comparison symbols overlaid on the chart (max 20, not persisted)
  compareSymbols: string[];
  addCompareSymbol: (symbol: string) => void;
  removeCompareSymbol: (symbol: string) => void;
  clearCompareSymbols: () => void;

  // Crosshair OHLCV values shown in header (not persisted)
  crosshair: CrosshairData | null;
  setCrosshair: (data: CrosshairData | null) => void;

  // Pixel position of the active crosshair's native Y-axis price label, so DOM
  // overlays (e.g. AxisValueLabels) can avoid rendering on top of it (not persisted)
  crosshairAxisY: { paneId: string; y: number } | null;
  setCrosshairAxisY: (v: { paneId: string; y: number } | null) => void;

  // Last loaded bar — used by legend when crosshair is inactive (not persisted)
  lastBar: CrosshairData | null;
  setLastBar: (data: CrosshairData | null) => void;

  // Drawing toolbar visibility (not persisted)
  drawingToolbarCollapsed: boolean;
  setDrawingToolbarCollapsed: (v: boolean) => void;

  // True while a drawing tool is armed or an overlay/label/text-box is being
  // edited — lets other UI (e.g. the nudge scheduler) avoid interrupting mid-task.
  drawingTaskActive: boolean;
  setDrawingTaskActive: (v: boolean) => void;

  // Right toolbar + panel visibility (not persisted)
  rightToolbarOpen: boolean;
  toggleRightToolbar: () => void;
  rightPanelActive: 'watchlist' | 'templates' | 'ai' | 'alerts' | null;
  setRightPanelActive: (panel: 'watchlist' | 'templates' | 'ai' | 'alerts' | null) => void;

  // Active chart instance (not persisted) — shared with DrawingToolbar, Header, modals
  chart: Chart | null;
  setChart: (c: Chart | null) => void;

  // True once the chart's first data load has finished *and* the locally
  // persisted indicators have been restored onto it — anything that rewrites
  // the chart's indicators (e.g. opening a shared link) must wait for this or
  // that restore would land on top of it. Reset whenever the chart changes
  // (not persisted).
  chartInitialized: boolean;
  setChartInitialized: (v: boolean) => void;

  // Symbol whose data is actually loaded in the chart — lags behind `symbol`
  // until applyNewData() completes (not persisted)
  loadedSymbol: string | null;
  setLoadedSymbol: (symbol: string) => void;

  // Which indicators are active: name → paneId (not persisted)
  indicatorPaneMap: Map<string, string>;
  setIndicatorPaneMap: (m: Map<string, string>) => void;

  // Indicators the user has hidden (sub-panes collapse to a title bar). Read by
  // rebalancePanes so a collapsed pane keeps its small height. On mobile, newly
  // added sub-pane indicators start hidden (see setIndicatorPaneMap). Not persisted.
  hiddenIndicators: Set<string>;
  setHiddenIndicators: (s: Set<string>) => void;

  // Pixel heights the user dragged sub-panes to (paneId → px), so rebalancePanes
  // keeps them instead of re-splitting space evenly. Not persisted.
  paneHeights: Record<string, number>;
  setPaneHeight: (paneId: string, height: number) => void;

  // User-drawn overlays (trendlines, fib, shapes…) and the symbol each was drawn
  // on — klinecharts has no "list all overlays" API, so this is how drawings are
  // found for sharing. Removed overlays are pruned lazily via getOverlayById
  // (not persisted).
  drawingOverlays: Array<{ id: string; symbol: string }>;
  addDrawingOverlay: (id: string, symbol: string) => void;
  clearDrawingOverlays: () => void;

  // Names of user-added indicators (persisted) — restored to chart on hard refresh
  activeIndicatorNames: string[];
  addActiveIndicator:    (name: string) => void;
  removeActiveIndicator: (name: string) => void;

  // Favorite indicators shown on the indicator toolbar (persisted)
  favoriteIndicatorNames: string[];
  addFavoriteIndicator:    (name: string) => void;
  removeFavoriteIndicator: (name: string) => void;

  // Corporate-action categories to backward-adjust candle prices for (persisted, synced to account)
  priceAdjustments: PriceAdjustmentType[];
  setPriceAdjustments: (types: PriceAdjustmentType[]) => void;

  // The unix-seconds timestamp of the leftmost visible bar (anchor for compare % calc, not persisted)
  compareAnchorTs: number;
  setCompareAnchorTs: (ts: number) => void;

  // Pixel height of the ChartLegend overlay — used to push canvas indicator labels below it
  legendHeight: number;
  setLegendHeight: (h: number) => void;

  // Chart wrapper DOM element — used for screenshot compositing (not persisted)
  chartWrapperEl: HTMLElement | null;
  setChartWrapperEl: (el: HTMLElement | null) => void;

  // Indicator values at current crosshair / last bar: name → { params, entries }
  indicatorValues: Record<string, { params: string; entries: Array<{ title: string; value: string }> }>;
  setIndicatorValues: (v: Record<string, { params: string; entries: Array<{ title: string; value: string }> }>) => void;

  // Indicator params overrides (not persisted): name → applied calcParams
  indicatorParams: Record<string, number[]>;
  setIndicatorParam: (name: string, params: number[]) => void;
  clearIndicatorParam: (name: string) => void;

  // Per-indicator line colours (persisted): name → array of hex strings matching lineLabels order
  indicatorColors: Record<string, string[]>;
  setIndicatorColors: (name: string, colors: string[]) => void;
  clearIndicatorColors: (name: string) => void;

  // Event-label scan configs (persisted): indicator name → active rule configs
  indicatorScanConfigs: Record<string, ScanRuleConfig[]>;
  setIndicatorScanConfigs: (indicatorName: string, configs: ScanRuleConfig[]) => void;
  clearAllIndicatorScanConfigs: () => void;

  // VOL label visibility (persisted)
  volumeLabelEnabled: boolean;
  setVolumeLabelEnabled: (enabled: boolean) => void;

  // Cross volume overlay visibility (persisted)
  crossVolumeEnabled: boolean;
  setCrossVolumeEnabled: (enabled: boolean) => void;

  // RSI overbought/oversold zone shading visibility (persisted)
  rsiZonesEnabled: boolean;
  setRsiZonesEnabled: (enabled: boolean) => void;

  // RSI divergence overlay visibility (persisted)
  rsiDivergenceEnabled: boolean;
  setRsiDivergenceEnabled: (enabled: boolean) => void;

  // RSI divergence detection parameters — [rsiPeriod, pivotLookback] (persisted)
  rsiDivergencePeriod: number;
  setRsiDivergencePeriod: (period: number) => void;
  rsiDivergencePivotLookback: number;
  setRsiDivergencePivotLookback: (lookback: number) => void;

  // VOL label per-bar overrides (persisted)
  volumeLabelOffsets: Record<string, { dx: number; dy: number }>;
  setVolumeLabelOffset: (key: string, offset: { dx: number; dy: number }) => void;
  volumeLabelHidden: Record<string, boolean>;
  setVolumeLabelHidden: (key: string, hidden: boolean) => void;

  // Saved indicator templates (persisted)
  indicatorTemplates: IndicatorTemplate[];
  saveIndicatorTemplate: (name: string, indicators: Array<{ name: string; params?: number[] }>, candleType?: string) => string;
  deleteIndicatorTemplate: (id: string) => void;
  updateIndicatorTemplate: (id: string, indicators: Array<{ name: string; params?: number[]; colors?: string[] }>, candleType?: string) => void;

  // Currently active template id — null when no template loaded (not persisted)
  activeTemplateId: string | null;
  setActiveTemplateId: (id: string | null) => void;

  // Create a default template if none exist (guest users)
  ensureDefaultTemplate: () => void;

  // Active candle type string (not persisted) — kept in sync so isDirty can react
  activeCandleType: string | undefined;
  setActiveCandleType: (ct: string | undefined) => void;

  // RS Line configs (persisted) — each entry drives one RS line + rating overlay
  rsLineConfigs: RSLineConfig[];
  addRSLine: (config: Omit<RSLineConfig, 'id'>) => void;
  removeRSLine: (id: string) => void;
  updateRSLine: (id: string, patch: Partial<Omit<RSLineConfig, 'id'>>) => void;
  setRSLineConfigs: (configs: RSLineConfig[]) => void;

  // EPS Line overlay — raw EPS on its own secondary axis (persisted)
  epsLineEnabled: boolean;
  setEpsLineEnabled: (enabled: boolean) => void;
  epsLineAlignToAnnouncement: boolean;
  setEpsLineAlignToAnnouncement: (enabled: boolean) => void;
  epsLineAxisScale: 'linear' | 'log';
  setEpsLineAxisScale: (scale: 'linear' | 'log') => void;
  epsLineForwardModel: 'ttm-yoy' | 'cagr-2y' | 'quarter-momentum';
  setEpsLineForwardModel: (model: 'ttm-yoy' | 'cagr-2y' | 'quarter-momentum') => void;
  epsLineShowForwardProjection: boolean;
  setEpsLineShowForwardProjection: (v: boolean) => void;

  // Foreign Holding sub-pane — a bar series (absolute shares held by foreign
  // investors, colored by comparison to the previous bar) plus two
  // configurable moving-average lines and price-divergence markers, all
  // sharing the bars' own native scale (persisted).
  foreignHoldingUpColor: string;
  setForeignHoldingUpColor: (v: string) => void;
  foreignHoldingDownColor: string;
  setForeignHoldingDownColor: (v: string) => void;
  foreignHoldingFlatColor: string;
  setForeignHoldingFlatColor: (v: string) => void;
  foreignHoldingMA1Period: number;
  setForeignHoldingMA1Period: (v: number) => void;
  foreignHoldingMA1Color: string;
  setForeignHoldingMA1Color: (v: string) => void;
  foreignHoldingMA2Period: number;
  setForeignHoldingMA2Period: (v: number) => void;
  foreignHoldingMA2Color: string;
  setForeignHoldingMA2Color: (v: string) => void;
  foreignHoldingConfirmBullColor: string;
  setForeignHoldingConfirmBullColor: (v: string) => void;
  foreignHoldingDistributionColor: string;
  setForeignHoldingDistributionColor: (v: string) => void;
  foreignHoldingAccumulationColor: string;
  setForeignHoldingAccumulationColor: (v: string) => void;
  foreignHoldingConfirmBearColor: string;
  setForeignHoldingConfirmBearColor: (v: string) => void;

  // Cup & Handle pattern overlay — detection params, persisted + auto-synced to account
  cupHandleEnabled: boolean;
  setCupHandleEnabled: (enabled: boolean) => void;
  cupHandleSettings: CupHandleSettings;
  setCupHandleSettings: (patch: Partial<CupHandleSettings>) => void;

  // William O'Neil / IBD sell signal overlay — six independent rule configs,
  // persisted + auto-synced to account
  williamOneilSellEnabled: boolean;
  setWilliamOneilSellEnabled: (enabled: boolean) => void;
  williamOneilSellSettings: WilliamOneilSellSettings;
  setWilliamOneilSellSettings: (patch: Partial<WilliamOneilSellSettings>) => void;

  // Callback to re-read candle_pane height after indicator pane add/remove (not persisted)
  refreshPricePaneHeight: () => void;
  setRefreshPricePaneHeight: (fn: () => void) => void;

  // Callback to re-read all pane top offsets for ChartLegend overlay positioning (not persisted)
  refreshPaneTops: () => void;
  setRefreshPaneTops: (fn: () => void) => void;

  // Removes all sub-pane indicators then re-adds at equal computed heights (not persisted)
  rebalancePanes: () => Promise<void>;
  setRebalancePanes: (fn: () => Promise<void>) => void;

  // Corporate events for the active symbol (not persisted)
  corporateEvents: CorporateEvent[];
  setCorporateEvents: (events: CorporateEvent[]) => void;

  // Hovered event marker — drives the EventTooltip overlay (not persisted)
  hoveredEvents: CorporateEvent[] | null;
  hoveredEventBar: { x: number; y: number } | null;
  setHoveredEvents: (events: CorporateEvent[] | null, pos: { x: number; y: number } | null) => void;

  // Pinned event tooltip — stays open until user clicks outside (not persisted)
  pinnedEvents: CorporateEvent[] | null;
  pinnedEventBar: { x: number; y: number } | null;
  setPinnedEvents: (events: CorporateEvent[] | null, pos: { x: number; y: number } | null) => void;

  // Hovered O'Neil sell-signal marker — drives the WilliamOneilSellTooltip overlay (not persisted)
  hoveredSellEvents: SellEvent[] | null;
  hoveredSellBar: { x: number; y: number } | null;
  hoveredSellDate: string | null;
  setHoveredSellEvents: (events: SellEvent[] | null, pos: { x: number; y: number } | null, date?: string | null) => void;

  // Pinned O'Neil sell-signal tooltip — stays open until user clicks outside (not persisted)
  pinnedSellEvents: SellEvent[] | null;
  pinnedSellBar: { x: number; y: number } | null;
  pinnedSellDate: string | null;
  setPinnedSellEvents: (events: SellEvent[] | null, pos: { x: number; y: number } | null, date?: string | null) => void;

  // Toggle to show/hide all event markers (persisted)
  showEvents: boolean;
  setShowEvents: (v: boolean) => void;

  // Price axis scale type (persisted)
  priceScaleType: 'normal' | 'log' | 'percentage';
  setPriceScaleType: (t: 'normal' | 'log' | 'percentage') => void;

  // Candle up/down colours (persisted)
  candleUpColor: string;
  setCandleUpColor: (color: string) => void;
  candleDownColor: string;
  setCandleDownColor: (color: string) => void;

  // HLC bar colours (persisted)
  hlcUpColor: string;
  setHlcUpColor: (color: string) => void;
  hlcDownColor: string;
  setHlcDownColor: (color: string) => void;

  // Grid visibility (persisted)
  showHGrid: boolean;
  setShowHGrid: (v: boolean) => void;
  showVGrid: boolean;
  setShowVGrid: (v: boolean) => void;

  // Price mark visibility (persisted)
  showPriceMarkHigh: boolean;
  setShowPriceMarkHigh: (v: boolean) => void;
  showPriceMarkLow: boolean;
  setShowPriceMarkLow: (v: boolean) => void;
  showLastPriceLine: boolean;
  setShowLastPriceLine: (v: boolean) => void;

  // Crosshair visibility (persisted)
  showCrosshair: boolean;
  setShowCrosshair: (v: boolean) => void;

  // Y-axis reverse (persisted)
  yAxisReverse: boolean;
  setYAxisReverse: (v: boolean) => void;

  // Selected period preset for the period bar (not persisted)
  chartPeriod: string | null;
  setChartPeriod: (period: string | null) => void;
  // Unix-seconds start date for data fetch (not persisted)
  chartFromDate: number | null;
  setChartFromDate: (ts: number | null) => void;

  // Called on logout — clears drawings, indicators, resets watchlist
  resetForLogout: () => void;
}

export const useChartStore = create<ChartState>()(
  persist(
    (set, get) => ({
      symbol:      'ASI',
      setSymbol:   (symbol) => { set({ symbol, compareSymbols: [] }); },

      timeframe:    '1D',
      setTimeframe: (timeframe) => set({ timeframe }),

      theme:       'dark',
      toggleTheme: () => {
        const newTheme = get().theme === 'dark' ? 'light' : 'dark';
        set({ theme: newTheme });
        // Sync to server for logged-in users (fire-and-forget)
        fetch('/api/user/settings', {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ theme: newTheme }),
        }).catch(() => {/* silent — user may not be logged in */});
      },

      watchlist: [
        { symbol: 'ASI',       name: 'All Share Price Index', type: 'index' },
        { symbol: 'S&P SL20', name: 'S&P SL20 Index',         type: 'index' },
      ],
      addToWatchlist: (entry) =>
        set((s) => {
          if (s.watchlist.some((w) => w.symbol === entry.symbol)) return s;
          return { watchlist: [...s.watchlist, entry] };
        }),
      removeFromWatchlist: (symbol) =>
        set((s) => ({ watchlist: s.watchlist.filter((w) => w.symbol !== symbol) })),
      setWatchlist: (entries) => set({ watchlist: entries }),

      compareSymbols: [],
      addCompareSymbol: (symbol) =>
        set((s) => {
          if (s.compareSymbols.includes(symbol) || s.symbol === symbol) return s;
          if (s.compareSymbols.length >= 20) return s;
          return { compareSymbols: [...s.compareSymbols, symbol] };
        }),
      removeCompareSymbol: (symbol) =>
        set((s) => ({ compareSymbols: s.compareSymbols.filter((x) => x !== symbol) })),
      clearCompareSymbols: () => set({ compareSymbols: [] }),

      crosshair:    null,
      setCrosshair: (crosshair) => set({ crosshair }),

      crosshairAxisY:    null,
      setCrosshairAxisY: (crosshairAxisY) => set({ crosshairAxisY }),

      lastBar:    null,
      setLastBar: (lastBar) => set({ lastBar }),

      drawingToolbarCollapsed: false,
      setDrawingToolbarCollapsed: (drawingToolbarCollapsed) => set({ drawingToolbarCollapsed }),

      drawingTaskActive: false,
      setDrawingTaskActive: (drawingTaskActive) => set({ drawingTaskActive }),

      rightToolbarOpen: true,
      toggleRightToolbar: () => set((s) => ({ rightToolbarOpen: !s.rightToolbarOpen })),
      rightPanelActive: 'watchlist',
      setRightPanelActive: (rightPanelActive) => set({ rightPanelActive }),

      chart: null,
      setChart: (chart) => set({ chart, chartInitialized: false }),
      chartInitialized: false,
      setChartInitialized: (chartInitialized) => set({ chartInitialized }),

      loadedSymbol: null,
      setLoadedSymbol: (loadedSymbol) => set({ loadedSymbol }),

      // VOL is always created on chart init by ChartContainer
      indicatorPaneMap: new Map([['VOL', 'vol_pane']]),
      // Derive activeIndicatorNames from the map so all call sites stay in sync
      // automatically — IndicatorBar, ChartLegend, template apply, restore loop.
      setIndicatorPaneMap: (indicatorPaneMap) => set((s) => {
        const hidden = new Set(s.hiddenIndicators);
        let hiddenChanged = false;
        // Drop hidden flags for indicators that are no longer on the chart.
        hidden.forEach((n) => {
          if (!indicatorPaneMap.has(n)) { hidden.delete(n); hiddenChanged = true; }
        });
        // Mobile: sub-panes can't be resized and crowd out the price pane, so
        // freshly added ones start collapsed; tapping the title expands them.
        if (isMobileViewport()) {
          indicatorPaneMap.forEach((_, n) => {
            if (!s.indicatorPaneMap.has(n) && isUserSubPaneIndicator(n)) { hidden.add(n); hiddenChanged = true; }
          });
        }
        // Forget dragged heights for panes that are gone.
        const livePaneIds = new Set(indicatorPaneMap.values());
        const staleHeightIds = Object.keys(s.paneHeights).filter((id) => !livePaneIds.has(id));
        let paneHeights = s.paneHeights;
        if (staleHeightIds.length > 0) {
          paneHeights = { ...s.paneHeights };
          staleHeightIds.forEach((id) => { delete paneHeights[id]; });
        }
        return {
          indicatorPaneMap,
          activeIndicatorNames: [...indicatorPaneMap.keys()].filter((k) => k !== 'VOL'),
          ...(hiddenChanged ? { hiddenIndicators: hidden } : {}),
          ...(paneHeights !== s.paneHeights ? { paneHeights } : {}),
        };
      }),
      hiddenIndicators: new Set<string>(),
      setHiddenIndicators: (hiddenIndicators) => set({ hiddenIndicators }),
      paneHeights: {},
      setPaneHeight: (paneId, height) => set((s) => ({ paneHeights: { ...s.paneHeights, [paneId]: height } })),

      drawingOverlays: [],
      addDrawingOverlay: (id, symbol) => set((s) => ({
        drawingOverlays: s.drawingOverlays.some((d) => d.id === id)
          ? s.drawingOverlays
          : [...s.drawingOverlays, { id, symbol }],
      })),
      clearDrawingOverlays: () => set({ drawingOverlays: [] }),

      activeIndicatorNames: [],
      addActiveIndicator: (name) => set((s) => ({
        activeIndicatorNames: s.activeIndicatorNames.includes(name)
          ? s.activeIndicatorNames
          : [...s.activeIndicatorNames, name],
      })),
      removeActiveIndicator: (name) => set((s) => ({
        activeIndicatorNames: s.activeIndicatorNames.filter((n) => n !== name),
      })),

      favoriteIndicatorNames: ['MA', 'EMA', 'RSI', 'VOL'],
      addFavoriteIndicator: (name) => {
        set((s) => ({
          favoriteIndicatorNames: s.favoriteIndicatorNames.includes(name)
            ? s.favoriteIndicatorNames
            : [...s.favoriteIndicatorNames, name],
        }));
        // Sync to server for logged-in users (fire-and-forget)
        const next = useChartStore.getState().favoriteIndicatorNames;
        fetch('/api/user/settings', {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ favoriteIndicators: next }),
        }).catch(() => {/* silent — user may not be logged in */});
      },
      removeFavoriteIndicator: (name) => {
        set((s) => ({
          favoriteIndicatorNames: s.favoriteIndicatorNames.filter((n) => n !== name),
        }));
        // Sync to server for logged-in users (fire-and-forget)
        const next = useChartStore.getState().favoriteIndicatorNames;
        fetch('/api/user/settings', {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ favoriteIndicators: next }),
        }).catch(() => {/* silent — user may not be logged in */});
      },

      priceAdjustments: ['splits'],
      setPriceAdjustments: (priceAdjustments) => {
        set({ priceAdjustments });
        // Sync to server for logged-in users (fire-and-forget)
        fetch('/api/user/settings', {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ chartSettings: { priceAdjustments } }),
        }).catch(() => {/* silent — user may not be logged in */});
      },

      compareAnchorTs: 0,
      setCompareAnchorTs: (compareAnchorTs) => set({ compareAnchorTs }),

      legendHeight: 0,
      setLegendHeight: (legendHeight) => set({ legendHeight }),

      chartWrapperEl: null,
      setChartWrapperEl: (chartWrapperEl) => set({ chartWrapperEl }),

      indicatorValues: {},
      setIndicatorValues: (indicatorValues) => set({ indicatorValues }),

      indicatorParams: {},
      setIndicatorParam: (name, params) => set((s) => ({ indicatorParams: { ...s.indicatorParams, [name]: params } })),
      clearIndicatorParam: (name) => set((s) => { const next = { ...s.indicatorParams }; delete next[name]; return { indicatorParams: next }; }),

      indicatorColors: {},
      setIndicatorColors: (name, colors) => set((s) => ({ indicatorColors: { ...s.indicatorColors, [name]: colors } })),
      clearIndicatorColors: (name) => set((s) => { const next = { ...s.indicatorColors }; delete next[name]; return { indicatorColors: next }; }),

      indicatorScanConfigs: {},
      setIndicatorScanConfigs: (indicatorName, configs) =>
        set((s) => ({ indicatorScanConfigs: { ...s.indicatorScanConfigs, [indicatorName]: configs } })),
      clearAllIndicatorScanConfigs: () => set({ indicatorScanConfigs: {} }),

      volumeLabelEnabled: false,
      setVolumeLabelEnabled: (volumeLabelEnabled) => set({ volumeLabelEnabled }),

      crossVolumeEnabled: false,
      setCrossVolumeEnabled: (crossVolumeEnabled) => set({ crossVolumeEnabled }),

      rsiZonesEnabled: true,
      setRsiZonesEnabled: (rsiZonesEnabled) => set({ rsiZonesEnabled }),

      rsiDivergenceEnabled: true,
      setRsiDivergenceEnabled: (rsiDivergenceEnabled) => set({ rsiDivergenceEnabled }),

      // Kept in sync with DEFAULT_RSI_PERIOD / DEFAULT_PIVOT_LOOKBACK in
      // indicators/rsiDivergence.ts (not imported — that module imports
      // useChartStore, so importing back here would be circular).
      rsiDivergencePeriod: 14,
      setRsiDivergencePeriod: (rsiDivergencePeriod) => set({ rsiDivergencePeriod }),

      rsiDivergencePivotLookback: 2,
      setRsiDivergencePivotLookback: (rsiDivergencePivotLookback) => set({ rsiDivergencePivotLookback }),

      volumeLabelOffsets: {},
      setVolumeLabelOffset: (key, offset) => set((s) => {
        const next = { ...s.volumeLabelOffsets };
        if (offset.dx === 0 && offset.dy === 0) {
          delete next[key];
        } else {
          next[key] = { dx: offset.dx, dy: offset.dy };
        }
        return { volumeLabelOffsets: next };
      }),
      volumeLabelHidden: {},
      setVolumeLabelHidden: (key, hidden) => set((s) => {
        const next = { ...s.volumeLabelHidden };
        if (hidden) {
          next[key] = true;
        } else {
          delete next[key];
        }
        return { volumeLabelHidden: next };
      }),

      indicatorTemplates: [],
      saveIndicatorTemplate: (name, indicators, candleType) => {
        const id = `tpl_${Date.now()}`;
        const { indicatorColors, indicatorScanConfigs, rsLineConfigs, volumeLabelEnabled, epsLineEnabled, epsLineAlignToAnnouncement, epsLineAxisScale, epsLineForwardModel, epsLineShowForwardProjection, foreignHoldingUpColor, foreignHoldingDownColor, foreignHoldingFlatColor, foreignHoldingMA1Period, foreignHoldingMA1Color, foreignHoldingMA2Period, foreignHoldingMA2Color, foreignHoldingConfirmBullColor, foreignHoldingDistributionColor, foreignHoldingAccumulationColor, foreignHoldingConfirmBearColor, cupHandleEnabled, cupHandleSettings, williamOneilSellEnabled, williamOneilSellSettings, candleUpColor, candleDownColor, hlcUpColor, hlcDownColor, showHGrid, showVGrid, showPriceMarkHigh, showPriceMarkLow, showLastPriceLine, showCrosshair, yAxisReverse, priceScaleType } = get();
        // Enrich each entry with persisted colours if available
        const enriched = indicators.map((ind) => ({
          ...ind,
          colors: indicatorColors[ind.name]?.length ? indicatorColors[ind.name] : undefined,
        }));
        const scanSnapshot = Object.keys(indicatorScanConfigs).length ? indicatorScanConfigs : undefined;
        const rsSnapshot = rsLineConfigs.length ? rsLineConfigs : undefined;
        const chartSettings = {
          ...(candleType ? { candleType } : {}),
          candleUpColor, candleDownColor, hlcUpColor, hlcDownColor,
          showHGrid, showVGrid, showPriceMarkHigh, showPriceMarkLow,
          showLastPriceLine, showCrosshair, yAxisReverse, priceScaleType,
        };
        set((s) => ({
          indicatorTemplates: [
            ...s.indicatorTemplates,
            { id, name, indicators: enriched, ...(scanSnapshot ? { scanConfigs: scanSnapshot } : {}), ...(rsSnapshot ? { rsLineConfigs: rsSnapshot } : {}), volumeLabelEnabled, epsLineEnabled, epsLineAlignToAnnouncement, epsLineAxisScale, epsLineForwardModel, epsLineShowForwardProjection, foreignHoldingUpColor, foreignHoldingDownColor, foreignHoldingFlatColor, foreignHoldingMA1Period, foreignHoldingMA1Color, foreignHoldingMA2Period, foreignHoldingMA2Color, foreignHoldingConfirmBullColor, foreignHoldingDistributionColor, foreignHoldingAccumulationColor, foreignHoldingConfirmBearColor, cupHandleEnabled, cupHandleSettings, williamOneilSellEnabled, williamOneilSellSettings, chartSettings },
          ],
        }));
        return id;
      },
      deleteIndicatorTemplate: (id) =>
        set((s) => ({ indicatorTemplates: s.indicatorTemplates.filter((t) => t.id !== id) })),
      updateIndicatorTemplate: (id, indicators, candleType) => {
        const { indicatorColors, indicatorScanConfigs, rsLineConfigs, volumeLabelEnabled, epsLineEnabled, epsLineAlignToAnnouncement, epsLineAxisScale, epsLineForwardModel, epsLineShowForwardProjection, foreignHoldingUpColor, foreignHoldingDownColor, foreignHoldingFlatColor, foreignHoldingMA1Period, foreignHoldingMA1Color, foreignHoldingMA2Period, foreignHoldingMA2Color, foreignHoldingConfirmBullColor, foreignHoldingDistributionColor, foreignHoldingAccumulationColor, foreignHoldingConfirmBearColor, cupHandleEnabled, cupHandleSettings, williamOneilSellEnabled, williamOneilSellSettings, candleUpColor, candleDownColor, hlcUpColor, hlcDownColor, showHGrid, showVGrid, showPriceMarkHigh, showPriceMarkLow, showLastPriceLine, showCrosshair, yAxisReverse, priceScaleType } = get();
        const enriched = indicators.map((ind) => ({
          ...ind,
          colors: indicatorColors[ind.name]?.length ? indicatorColors[ind.name] : ind.colors,
        }));
        const scanSnapshot = Object.keys(indicatorScanConfigs).length ? indicatorScanConfigs : undefined;
        const rsSnapshot = rsLineConfigs.length ? rsLineConfigs : undefined;
        const chartSettings = {
          ...(candleType !== undefined ? { candleType } : {}),
          candleUpColor, candleDownColor, hlcUpColor, hlcDownColor,
          showHGrid, showVGrid, showPriceMarkHigh, showPriceMarkLow,
          showLastPriceLine, showCrosshair, yAxisReverse, priceScaleType,
        };
        set((s) => ({
          indicatorTemplates: s.indicatorTemplates.map((t) =>
            t.id === id
              ? { ...t, indicators: enriched, ...(scanSnapshot !== undefined ? { scanConfigs: scanSnapshot } : { scanConfigs: undefined }), ...(rsSnapshot !== undefined ? { rsLineConfigs: rsSnapshot } : { rsLineConfigs: undefined }), volumeLabelEnabled, epsLineEnabled, epsLineAlignToAnnouncement, epsLineAxisScale, epsLineForwardModel, epsLineShowForwardProjection, foreignHoldingUpColor, foreignHoldingDownColor, foreignHoldingFlatColor, foreignHoldingMA1Period, foreignHoldingMA1Color, foreignHoldingMA2Period, foreignHoldingMA2Color, foreignHoldingConfirmBullColor, foreignHoldingDistributionColor, foreignHoldingAccumulationColor, foreignHoldingConfirmBearColor, cupHandleEnabled, cupHandleSettings, williamOneilSellEnabled, williamOneilSellSettings, chartSettings }
              : t
          ),
        }));
      },

      activeTemplateId: null,
      setActiveTemplateId: (activeTemplateId) => set({ activeTemplateId }),

      ensureDefaultTemplate: () => {
        const { indicatorTemplates } = get();
        if (indicatorTemplates.length > 0) return;
        const defaultTemplate: IndicatorTemplate = {
          id: 'tpl_default',
          name: 'Default',
          indicators: [{ name: 'VOL' }],
          isDefault: true,
          chartSettings: {
            candleUpColor: '#26a69a',
            candleDownColor: '#ef5350',
            hlcUpColor: '#22C55E',
            hlcDownColor: '#EF4444',
            showHGrid: true,
            showVGrid: true,
            showPriceMarkHigh: true,
            showPriceMarkLow: true,
            showLastPriceLine: true,
            showCrosshair: true,
            yAxisReverse: false,
            priceScaleType: 'normal',
          },
        };
        set({ indicatorTemplates: [defaultTemplate], activeTemplateId: defaultTemplate.id });
      },

      activeCandleType: undefined,
      setActiveCandleType: (activeCandleType) => set({ activeCandleType }),

      rsLineConfigs: [
        { id: 'rs_asi',   benchmarkSymbol: 'ASI',      benchmarkName: 'All Share Price Index', color: '#2196F3', visible: false, offsetPx: 0 },
        { id: 'rs_sl20',  benchmarkSymbol: 'S&P SL20', benchmarkName: 'S&P SL20 Index',        color: '#FF9600', visible: false, offsetPx: 0 },
      ],
      addRSLine: (config) =>
        set((s) => ({
          rsLineConfigs: [
            ...s.rsLineConfigs,
            { ...config, id: `rs_${Date.now()}_${Math.random().toString(36).slice(2, 7)}` },
          ],
        })),
      removeRSLine: (id) =>
        set((s) => ({ rsLineConfigs: s.rsLineConfigs.filter((c) => c.id !== id) })),
      updateRSLine: (id, patch) =>
        set((s) => ({
          rsLineConfigs: s.rsLineConfigs.map((c) => (c.id === id ? { ...c, ...patch } : c)),
        })),
      setRSLineConfigs: (rsLineConfigs) => set({ rsLineConfigs }),

      epsLineEnabled: false,
      setEpsLineEnabled: (epsLineEnabled) => set({ epsLineEnabled }),
      epsLineAlignToAnnouncement: true,
      setEpsLineAlignToAnnouncement: (epsLineAlignToAnnouncement) => set({ epsLineAlignToAnnouncement }),
      epsLineAxisScale: 'log',
      setEpsLineAxisScale: (epsLineAxisScale) => set({ epsLineAxisScale }),
      epsLineForwardModel: 'ttm-yoy',
      setEpsLineForwardModel: (epsLineForwardModel) => set({ epsLineForwardModel }),
      epsLineShowForwardProjection: false,
      setEpsLineShowForwardProjection: (epsLineShowForwardProjection) => set({ epsLineShowForwardProjection }),

      foreignHoldingUpColor: '#22C55E',
      setForeignHoldingUpColor: (foreignHoldingUpColor) => set({ foreignHoldingUpColor }),
      foreignHoldingDownColor: '#EF4444',
      setForeignHoldingDownColor: (foreignHoldingDownColor) => set({ foreignHoldingDownColor }),
      foreignHoldingFlatColor: '#9CA3AF',
      setForeignHoldingFlatColor: (foreignHoldingFlatColor) => set({ foreignHoldingFlatColor }),
      foreignHoldingMA1Period: 10,
      setForeignHoldingMA1Period: (foreignHoldingMA1Period) => set({ foreignHoldingMA1Period }),
      foreignHoldingMA1Color: '#3B82F6',
      setForeignHoldingMA1Color: (foreignHoldingMA1Color) => set({ foreignHoldingMA1Color }),
      foreignHoldingMA2Period: 20,
      setForeignHoldingMA2Period: (foreignHoldingMA2Period) => set({ foreignHoldingMA2Period }),
      foreignHoldingMA2Color: '#EC4899',
      setForeignHoldingMA2Color: (foreignHoldingMA2Color) => set({ foreignHoldingMA2Color }),
      foreignHoldingConfirmBullColor: '#22C55E',
      setForeignHoldingConfirmBullColor: (foreignHoldingConfirmBullColor) => set({ foreignHoldingConfirmBullColor }),
      foreignHoldingDistributionColor: '#F59E0B',
      setForeignHoldingDistributionColor: (foreignHoldingDistributionColor) => set({ foreignHoldingDistributionColor }),
      foreignHoldingAccumulationColor: '#8B5CF6',
      setForeignHoldingAccumulationColor: (foreignHoldingAccumulationColor) => set({ foreignHoldingAccumulationColor }),
      foreignHoldingConfirmBearColor: '#EF4444',
      setForeignHoldingConfirmBearColor: (foreignHoldingConfirmBearColor) => set({ foreignHoldingConfirmBearColor }),

      cupHandleEnabled: false,
      setCupHandleEnabled: (cupHandleEnabled) => set({ cupHandleEnabled }),
      cupHandleSettings: DEFAULT_CUP_HANDLE_SETTINGS,
      setCupHandleSettings: (patch) => {
        const next = { ...get().cupHandleSettings, ...patch };
        set({ cupHandleSettings: next });
        // Sync to server for logged-in users (fire-and-forget, debounced so
        // dragging a slider doesn't fire a PATCH per pixel).
        if (cupHandleSyncTimer) clearTimeout(cupHandleSyncTimer);
        cupHandleSyncTimer = setTimeout(() => {
          fetch('/api/user/settings', {
            method: 'PATCH',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ chartSettings: { cupHandleSettings: next } }),
          }).catch(() => {/* silent — user may not be logged in */});
        }, 500);
      },

      williamOneilSellEnabled: false,
      setWilliamOneilSellEnabled: (williamOneilSellEnabled) => set({ williamOneilSellEnabled }),
      williamOneilSellSettings: DEFAULT_WILLIAM_ONEIL_SELL_SETTINGS,
      setWilliamOneilSellSettings: (patch) => {
        const next = { ...get().williamOneilSellSettings, ...patch };
        set({ williamOneilSellSettings: next });
        if (williamOneilSellSyncTimer) clearTimeout(williamOneilSellSyncTimer);
        williamOneilSellSyncTimer = setTimeout(() => {
          fetch('/api/user/settings', {
            method: 'PATCH',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ chartSettings: { williamOneilSellSettings: next } }),
          }).catch(() => {/* silent — user may not be logged in */});
        }, 500);
      },

      refreshPricePaneHeight: () => { /* registered by ChartContainer */ },
      setRefreshPricePaneHeight: (fn) => set({ refreshPricePaneHeight: fn }),

      refreshPaneTops: () => { /* registered by ChartLegend */ },
      setRefreshPaneTops: (fn) => set({ refreshPaneTops: fn }),

      rebalancePanes: async () => { /* registered by ChartContainer */ },
      setRebalancePanes: (fn) => set({ rebalancePanes: fn }),

      corporateEvents: [],
      setCorporateEvents: (corporateEvents) => set({ corporateEvents }),

      hoveredEvents: null,
      hoveredEventBar: null,
      setHoveredEvents: (hoveredEvents, hoveredEventBar) => set({ hoveredEvents, hoveredEventBar }),

      pinnedEvents: null,
      pinnedEventBar: null,
      setPinnedEvents: (pinnedEvents, pinnedEventBar) => set({ pinnedEvents, pinnedEventBar }),

      hoveredSellEvents: null,
      hoveredSellBar: null,
      hoveredSellDate: null,
      setHoveredSellEvents: (hoveredSellEvents, hoveredSellBar, hoveredSellDate = null) => set({ hoveredSellEvents, hoveredSellBar, hoveredSellDate }),

      pinnedSellEvents: null,
      pinnedSellBar: null,
      pinnedSellDate: null,
      setPinnedSellEvents: (pinnedSellEvents, pinnedSellBar, pinnedSellDate = null) => set({ pinnedSellEvents, pinnedSellBar, pinnedSellDate }),

      showEvents: true,
      setShowEvents: (showEvents) => set({ showEvents }),

      priceScaleType: 'normal',
      setPriceScaleType: (priceScaleType) => set({ priceScaleType }),

      candleUpColor: '#26a69a',
      setCandleUpColor: (candleUpColor) => set({ candleUpColor }),
      candleDownColor: '#ef5350',
      setCandleDownColor: (candleDownColor) => set({ candleDownColor }),

      hlcUpColor: '#22C55E',
      setHlcUpColor: (hlcUpColor) => set({ hlcUpColor }),
      hlcDownColor: '#EF4444',
      setHlcDownColor: (hlcDownColor) => set({ hlcDownColor }),

      showHGrid: true,
      setShowHGrid: (showHGrid) => set({ showHGrid }),
      showVGrid: true,
      setShowVGrid: (showVGrid) => set({ showVGrid }),

      showPriceMarkHigh: true,
      setShowPriceMarkHigh: (showPriceMarkHigh) => set({ showPriceMarkHigh }),
      showPriceMarkLow: true,
      setShowPriceMarkLow: (showPriceMarkLow) => set({ showPriceMarkLow }),
      showLastPriceLine: true,
      setShowLastPriceLine: (showLastPriceLine) => set({ showLastPriceLine }),

      showCrosshair: true,
      setShowCrosshair: (showCrosshair) => set({ showCrosshair }),

      yAxisReverse: false,
      setYAxisReverse: (yAxisReverse) => set({ yAxisReverse }),

      chartPeriod: null,
      setChartPeriod: (chartPeriod) => set({ chartPeriod }),
      chartFromDate: null,
      setChartFromDate: (chartFromDate) => set({ chartFromDate }),

      resetForLogout: () => {        const { chart, indicatorPaneMap } = get();
        if (chart) {
          // Remove all drawings/overlays
          chart.removeOverlay();
          // Remove all indicators except VOL
          indicatorPaneMap.forEach((paneId, name) => {
            if (name !== 'VOL') {
              chart.removeIndicator(paneId, name);
            }
          });
        }
        set({
          watchlist: [
            { symbol: 'ASI',      name: 'All Share Price Index', type: 'index' },
            { symbol: 'S&P SL20', name: 'S&P SL20 Index',        type: 'index' },
          ],
          indicatorPaneMap:     new Map([['VOL', 'vol_pane']]),
          hiddenIndicators:     new Set<string>(),
          paneHeights:          {},
          drawingOverlays:      [],
          compareSymbols:       [],
          activeTemplateId:     null,
          activeIndicatorNames: [],
          favoriteIndicatorNames: ['MA', 'EMA', 'RSI', 'VOL'],
          priceAdjustments:     ['splits'],
          cupHandleEnabled:     false,
          cupHandleSettings:    DEFAULT_CUP_HANDLE_SETTINGS,
          williamOneilSellEnabled:  false,
          williamOneilSellSettings: DEFAULT_WILLIAM_ONEIL_SELL_SETTINGS,
          chartPeriod:          null,
          chartFromDate:        null,
          activeCandleType:     undefined,
          candleUpColor:        '#26a69a',
          candleDownColor:      '#ef5350',
          hlcUpColor:           '#22C55E',
          hlcDownColor:         '#EF4444',
          showHGrid:            true,
          showVGrid:            true,
          showPriceMarkHigh:    true,
          showPriceMarkLow:     true,
          showLastPriceLine:    true,
          showCrosshair:        true,
          yAxisReverse:         false,
        });
      },
    }),
    {
      name: 'cseview-chart-store',
      partialize: (s) => ({
        symbol:               s.symbol,
        timeframe:            s.timeframe,
        theme:                s.theme,
        watchlist:            s.watchlist,
        indicatorTemplates:      s.indicatorTemplates,
        indicatorParams:         s.indicatorParams,
        indicatorColors:         s.indicatorColors,
        indicatorScanConfigs:    s.indicatorScanConfigs,
        volumeLabelEnabled:      s.volumeLabelEnabled,
        crossVolumeEnabled:      s.crossVolumeEnabled,
        rsiZonesEnabled:         s.rsiZonesEnabled,
        rsiDivergenceEnabled:    s.rsiDivergenceEnabled,
        rsiDivergencePeriod:        s.rsiDivergencePeriod,
        rsiDivergencePivotLookback: s.rsiDivergencePivotLookback,
        rsLineConfigs:           s.rsLineConfigs,
        activeIndicatorNames:    s.activeIndicatorNames,
        favoriteIndicatorNames:  s.favoriteIndicatorNames,
        priceAdjustments:        s.priceAdjustments,
        showEvents:              s.showEvents,
        priceScaleType:          s.priceScaleType,
        epsLineEnabled:              s.epsLineEnabled,
        epsLineAlignToAnnouncement:  s.epsLineAlignToAnnouncement,
        epsLineAxisScale:            s.epsLineAxisScale,
        epsLineForwardModel:         s.epsLineForwardModel,
        epsLineShowForwardProjection: s.epsLineShowForwardProjection,
        foreignHoldingUpColor:            s.foreignHoldingUpColor,
        foreignHoldingDownColor:          s.foreignHoldingDownColor,
        foreignHoldingFlatColor:          s.foreignHoldingFlatColor,
        foreignHoldingMA1Period:          s.foreignHoldingMA1Period,
        foreignHoldingMA1Color:           s.foreignHoldingMA1Color,
        foreignHoldingMA2Period:          s.foreignHoldingMA2Period,
        foreignHoldingMA2Color:           s.foreignHoldingMA2Color,
        foreignHoldingConfirmBullColor:   s.foreignHoldingConfirmBullColor,
        foreignHoldingDistributionColor:  s.foreignHoldingDistributionColor,
        foreignHoldingAccumulationColor:  s.foreignHoldingAccumulationColor,
        foreignHoldingConfirmBearColor:   s.foreignHoldingConfirmBearColor,
        cupHandleEnabled:   s.cupHandleEnabled,
        cupHandleSettings:  s.cupHandleSettings,
        williamOneilSellEnabled:  s.williamOneilSellEnabled,
        williamOneilSellSettings: s.williamOneilSellSettings,
        candleUpColor:      s.candleUpColor,
        candleDownColor:    s.candleDownColor,
        hlcUpColor:         s.hlcUpColor,
        hlcDownColor:       s.hlcDownColor,
        showHGrid:          s.showHGrid,
        showVGrid:          s.showVGrid,
        showPriceMarkHigh:  s.showPriceMarkHigh,
        showPriceMarkLow:   s.showPriceMarkLow,
        showLastPriceLine:  s.showLastPriceLine,
        showCrosshair:      s.showCrosshair,
        yAxisReverse:       s.yAxisReverse,
        activeCandleType:   s.activeCandleType,
      }),
    }
  )
);
